import { useLoaderStore } from '@/store/use-loader-store';
import {
  isAuth0Configured,
  isEmbeddedContext,
  isTeamsContext,
} from '@/utils/auth-context';
import { Auth0SocialConnection } from '@/utils/auth0-connections';
import {
  resolveEnterpriseSsoOrganization,
  resolveSocialLoginOrganization,
} from '@/utils/auth0-organization';
import { getSoulMapSocialOrganization } from '@/utils/soulmap-auth';
import {
  clearTeamsSsoHandoff,
  clearTeamsSsoInProgress,
  consumeActivationSsoReturnPath,
  consumeSsoSessionIntent,
  markSsoSessionIntent,
  markTeamsSsoHandoff,
  markTeamsSsoInProgress,
  markActivationSsoReturnPath,
  markTeamsSsoReloading,
} from '@/utils/sso-session-guard';
import { authenticateWithTeams } from '@/utils/teams-auth';
import {
  hasAuth0CacheInStorage,
  isTeamsAuthPopupCancelledError,
  waitForAuth0Cache,
} from '@/utils/teams-sso-handoff';
import {
  useAuth0,
  type PopupLoginOptions,
  type RedirectLoginOptions,
} from '@auth0/auth0-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

function getAuth0ErrorCode(error: unknown): string {
  if (error && typeof error === 'object') {
    const record = error as { error?: unknown; message?: unknown };
    if (typeof record.error === 'string') {
      return record.error;
    }
    if (typeof record.message === 'string') {
      return record.message;
    }
  }
  return '';
}

function getAuth0ErrorMessage(error: unknown): string {
  const code = getAuth0ErrorCode(error).toLowerCase();
  if (
    code.includes('interaction_required') ||
    code.includes('login_required') ||
    code.includes('consent_required')
  ) {
    return 'Please sign in again to continue.';
  }

  if (error instanceof Error) {
    if (error.message.includes('popup closed')) {
      return 'Sign-in was cancelled before it finished.';
    }
    return error.message;
  }
  return 'Sign-in didn’t finish. Please try again.';
}

export function formatAuth0CallbackError(description: string): string {
  const normalized = description.toLowerCase();

  if (normalized.includes('connection is not enabled')) {
    return 'Google sign-in isn’t available right now. Please try again in a few minutes.';
  }

  if (
    normalized.includes('interaction_required') ||
    normalized.includes('login_required') ||
    normalized.includes('consent_required')
  ) {
    return 'Please sign in again to continue.';
  }

  return description;
}

type SsoLoginOptions = Omit<RedirectLoginOptions, 'authorizationParams'> & {
  authorizationParams?: RedirectLoginOptions['authorizationParams'];
};

function resolveSocialOrganization(
  organization?: string | null,
  envOrganizationOnly = false
): string | null {
  if (envOrganizationOnly) {
    return getSoulMapSocialOrganization();
  }

  return resolveSocialLoginOrganization({
    queryOrg: organization,
    envOrganizationOnly: false,
  });
}

function isSocialOrganizationRequired(): boolean {
  return import.meta.env.VITE_AUTH0_SOCIAL_REQUIRES_ORG !== 'false';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function completeTeamsSsoHandoff(
  getAccessTokenSilently: () => Promise<string>
): Promise<void> {
  markTeamsSsoHandoff();
  useLoaderStore.getState().setIsLoading(true);

  if (!hasAuth0CacheInStorage()) {
    await waitForAuth0Cache(8, 50);
  }

  if (hasAuth0CacheInStorage()) {
    markTeamsSsoReloading();
    window.location.reload();
    return;
  }

  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      await getAccessTokenSilently();
      markTeamsSsoReloading();
      window.location.reload();
      return;
    } catch {
      await sleep(50);
    }
  }

  useLoaderStore.getState().setIsLoading(false);
  throw new Error(
    'Sign-in finished in the popup, but this app could not read your session. Please try again.'
  );
}

async function runTeamsAuthenticate(
  getAccessTokenSilently: () => Promise<string>,
  organization: string | null | undefined,
  setIsTeamsAuthenticating: (loading: boolean) => void
): Promise<void> {
  markTeamsSsoInProgress();
  setIsTeamsAuthenticating(true);

  try {
    try {
      await authenticateWithTeams({ organization });
    } catch (error) {
      if (isTeamsAuthPopupCancelledError(error)) {
        setIsTeamsAuthenticating(false);
        markTeamsSsoHandoff();
        useLoaderStore.getState().setIsLoading(true);

        if (!hasAuth0CacheInStorage()) {
          await waitForAuth0Cache(5, 50);
        }

        if (hasAuth0CacheInStorage()) {
          markTeamsSsoReloading();
          window.location.reload();
          return;
        }

        try {
          await getAccessTokenSilently();
          markTeamsSsoReloading();
          window.location.reload();
          return;
        } catch {
          clearTeamsSsoHandoff();
          useLoaderStore.getState().setIsLoading(false);
          // Fall through to the original error when recovery fails.
        }
      }

      throw error;
    }

    setIsTeamsAuthenticating(false);
    await completeTeamsSsoHandoff(getAccessTokenSilently);
  } catch (error) {
    clearTeamsSsoInProgress();
    clearTeamsSsoHandoff();
    setIsTeamsAuthenticating(false);
    useLoaderStore.getState().setIsLoading(false);
    throw error;
  }
}

export function useSsoLogin() {
  const { loginWithPopup, loginWithRedirect, isLoading, getAccessTokenSilently } =
    useAuth0();
  const [isTeamsAuthenticating, setIsTeamsAuthenticating] = useState(false);

  const buildEnterprisePopupOptions = (
    options?: SsoLoginOptions
  ): PopupLoginOptions => {
    const explicitOrg =
      typeof options?.authorizationParams?.organization === 'string'
        ? options.authorizationParams.organization
        : null;
    const organization = resolveEnterpriseSsoOrganization({
      queryOrg: explicitOrg,
    });

    return {
      ...options,
      authorizationParams: {
        prompt: 'login',
        ...options?.authorizationParams,
        ...(organization ? { organization } : {}),
      },
    };
  };

  const buildSocialPopupOptions = (
    connection: Auth0SocialConnection,
    orgId: string | null
  ): PopupLoginOptions => ({
    authorizationParams: {
      connection,
      ...(orgId ? { organization: orgId } : {}),
    },
  });

  const loginWithSso = async (options?: SsoLoginOptions) => {
    // A plain sign-in must not inherit an abandoned invitation's return path.
    consumeActivationSsoReturnPath();
    if (!isAuth0Configured()) {
      toast.error(
        'Auth0 is not configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID in .env'
      );
      throw new Error('Auth0 is not configured');
    }

    try {
      if (isTeamsContext()) {
        markSsoSessionIntent();
        const explicitOrg =
          typeof options?.authorizationParams?.organization === 'string'
            ? options.authorizationParams.organization
            : null;
        const organization = resolveEnterpriseSsoOrganization({
          queryOrg: explicitOrg,
        });
        await runTeamsAuthenticate(
          getAccessTokenSilently,
          organization,
          setIsTeamsAuthenticating
        );
        return;
      }

      markSsoSessionIntent();
      await loginWithPopup(buildEnterprisePopupOptions(options));
    } catch (error) {
      console.error('SSO login failed:', error);
      toast.error(getAuth0ErrorMessage(error));
      throw error;
    }
  };

  const loginWithSocialConnection = async (
    connection: Auth0SocialConnection,
    options?: {
      organization?: string | null;
      /** SoulMap login — always use VITE_AUTH0_SOCIAL_ORGANIZATION. */
      envOrganizationOnly?: boolean;
    }
  ) => {
    consumeActivationSsoReturnPath();
    if (!isAuth0Configured()) {
      toast.error('Sign-in isn’t available right now. Please try again later.');
      throw new Error('Auth0 is not configured');
    }

    try {
      if (isTeamsContext()) {
        markSsoSessionIntent();
        const orgId = resolveSocialOrganization(
          options?.organization,
          options?.envOrganizationOnly ?? false
        );
        await runTeamsAuthenticate(
          getAccessTokenSilently,
          orgId,
          setIsTeamsAuthenticating
        );
        return;
      }

      const envOrganizationOnly = options?.envOrganizationOnly ?? false;
      const orgId = resolveSocialOrganization(
        options?.organization,
        envOrganizationOnly
      );
      const orgRequired = envOrganizationOnly || isSocialOrganizationRequired();

      if (orgRequired && !orgId) {
        toast.error(
          'Google sign-in isn’t available right now. Please try again in a few minutes.'
        );
        throw new Error(
          'Google sign-in isn’t available right now. Please try again in a few minutes.'
        );
      }

      markSsoSessionIntent();
      await loginWithPopup(buildSocialPopupOptions(connection, orgId));
    } catch (error) {
      console.error('Social login failed:', error);
      toast.error(getAuth0ErrorMessage(error));
      throw error;
    }
  };

  const loginWithActivationSso = async (options?: {
    organization?: string | null;
    loginHint?: string | null;
  }) => {
    if (!isAuth0Configured()) {
      toast.error(
        'Auth0 is not configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID in .env'
      );
      throw new Error('Auth0 is not configured');
    }

    const organization = resolveEnterpriseSsoOrganization({
      queryOrg: options?.organization,
    });
    const loginHint = options?.loginHint?.trim();

    try {
      if (isTeamsContext()) {
        markSsoSessionIntent();
        await runTeamsAuthenticate(
          getAccessTokenSilently,
          organization,
          setIsTeamsAuthenticating
        );
        return;
      }

      const returnTo = `${window.location.pathname}${window.location.search}`;
      markSsoSessionIntent();
      markActivationSsoReturnPath(returnTo);
      await loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          prompt: 'select_account',
          max_age: 0,
          ...(organization ? { organization } : {}),
          ...(loginHint ? { login_hint: loginHint } : {}),
        },
        ...(isEmbeddedContext()
          ? {
              openUrl(url: string) {
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.target = '_top';
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
              },
            }
          : {}),
      });
    } catch (error) {
      consumeSsoSessionIntent();
      console.error('Activation SSO login failed:', error);
      toast.error(getAuth0ErrorMessage(error));
      throw error;
    }
  };

  return {
    loginWithSso,
    loginWithSocialConnection,
    loginWithActivationSso,
    isLoading: isTeamsContext() ? isTeamsAuthenticating : isLoading,
  };
}
